import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { FaroTextButton } from "@components/common/faro-text-button";
import { ADD_TO_TEAM_TITLE } from "@components/teams/add-to-team/add-to-team-constants";
import { AddToTeamNotes } from "@components/teams/add-to-team/add-to-team-notes";
import { TeamSelect } from "@components/teams/add-to-team/team-select";
import { useAddMemberToTeam } from "@components/teams/add-to-team/use-add-member-to-team";
import { MemberTeamsLoadError } from "@components/teams/add-to-team/member-teams-load-error";
import { useMembersTeamIds } from "@components/teams/add-to-team/use-members-team-ids";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { Dialog, Text, useToast } from "@flat-ui";
import { useTeamUtils } from "@hooks/use-team-utils";
import { Stack } from "@mui-wrapper";
import { TeamTabs } from "@router/route-params";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { TeamEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
/**
 * Dialog that adds a single workspace member to a team, after the team is picked.
 *
 * It shares the team picker, the notes and the lookup of the member's teams with the bulk action
 * of the members table. The teams the member is in already are shown disabled in the picker, like
 * the invite dialog of the team page does.
 */
export function AddToTeamDialog({
  member,
  isOpen,
  onClose
}) {
  const addMemberToTeam = useAddMemberToTeam();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast,
    closeToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    openTeamDetails
  } = useTeamUtils();
  const [team, setTeam] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const memberTeams = useMembersTeamIds([member], isOpen);
  // Until the teams of the member are known, no team is known to be safe to pick
  const canPickTeam = !memberTeams.isLoading && !memberTeams.hasFailed;
  return <Dialog title={ADD_TO_TEAM_TITLE} confirmText="Add" open={isOpen} onConfirm={addToTeam} showSpinner={isLoading} isConfirmDisabled={!team || !canPickTeam} onCancel={closeDialog} dataTestId="add-to-team-dialog">
      <Stack sx={{
      gap: SPACE_ELEMENTS_OF_MODAL
    }}>
        <Text variant="bodyM">
          Select a {TEAM_DISPLAY_NAME} to add the selected member to.
        </Text>

        {memberTeams.hasFailed && <MemberTeamsLoadError numberOfMembers={1} onRetry={memberTeams.retry} />}

        <TeamSelect selectedTeamId={team?.id} onTeamChange={setTeam} isDisabled={isLoading || !canPickTeam} disabledTeamIds={memberTeams.teamIdsByMember.get(member.identity)} />

        {team && <AddToTeamNotes team={team} members={[member]} />}
      </Stack>
    </Dialog>;

  /** Closes the dialog and forgets the picked team */
  function closeDialog() {
    setTeam(undefined);
    setIsLoading(false);
    onClose();
  }

  /** Adds the member to the picked team */
  async function addToTeam() {
    if (!team) {
      return;
    }
    setIsLoading(true);
    trackEvent({
      name: TeamEvents.inviteMember,
      props: {
        numberOfMembers: 1,
        location: "workspace-members-table"
      }
    });
    try {
      await addMemberToTeam({
        teamId: team.id,
        member
      });

      // A key of its own, so that following the link closes this toast and no other
      const toastKey = `addMemberToTeam-${Date.now().toString()}`;
      openToast({
        key: toastKey,
        title: `Added to ${TEAM_DISPLAY_NAME}`,
        message: <Stack sx={{
          alignItems: "flex-start",
          gap: 0.5
        }}>
            <span>
              The member was added to the <var>"{team.name}"</var>{" "}
              {TEAM_DISPLAY_NAME}.
            </span>
            {/* Shown below the message, which the toast's own action button cannot do */}
            <FaroTextButton onClick={() => {
            closeToast(toastKey);
            openTeamDetails({
              teamId: team.id,
              // The picked team was copied before the member joined it
              numberOfMembers: team.memberCount + 1,
              tab: TeamTabs.members
            });
          }} sx={{
            p: 0.5,
            minWidth: 0
          }}>
              Go to {TEAM_DISPLAY_NAME}
            </FaroTextButton>
          </Stack>
      });
      closeDialog();
    } catch (error) {
      setIsLoading(false);
      handleErrorWithToast({
        id: `addMemberToTeam-${Date.now().toString()}`,
        title: `Cannot add the member to the ${TEAM_DISPLAY_NAME}`,
        error
      });
    }
  }
}